import React, { FC, useContext, ReactNode } from "react";

import {
  IssueProvider,
  IssueProviderContext,
} from "@adsk/acc-issues-core/providers/IssueProvider";
import {
  CurrentUserPermissionLevel,
  Issue,
  IssueSortingDirection,
  IssueSortingField,
} from "@adsk/acc-issues-types";
import { createSDK } from "@adsk/acc-issues-sdk";
import { ReferencePickerPlugin } from "@adsk/acc-reference-picker";
import { TOOL_IDS, useCurrentProjectId } from "@adsk/alloy-provider";
import { CheckmarkCircleIcon } from "@adsk/alloy-react-icon";

import { FiltersPanel } from "./FiltersPanel";
import IssueActivityCard from "./IssueActivityCard";
import { IssueEntityCellItem } from "./IssueEntityCellItem";
import IssuesCustomDataView from "./IssuesCustomDataView";
import IssuesEmptyState from "./IssuesEmptyState";
import { IssuesEntityBlock } from "./IssuesEntityBlock";
import { useTranslations } from "@adsk/acc-issues-translations";
import { CreateNewEntityPanel } from "./CreateNewEntityPanel";
import { TabTitle } from "@adsk/exo-skeleton-react";

const Provider: FC = ({ children }) => {
  const projectId = useCurrentProjectId();
  const isInIssueProviderContext = useContext(IssueProviderContext);

  if (!projectId || isInIssueProviderContext) {
    return <>{children}</>;
  }
  return <IssueProvider projectId={projectId}>{children}</IssueProvider>;
};
Provider.displayName = "Provider";

export const issuesReferencePickerPlugin: ReferencePickerPlugin<Issue> = {
  metadata: {
    domain: "autodesk-bim360-issue",
    type: "issue",
  },
  renderIcon: p => <CheckmarkCircleIcon {...p} />,
  toolId: TOOL_IDS.ISSUES,
  showSearchBox: true,
  is3LOSupported: true,
  PluralText: ({ lowerCase }) => {
    const { T } = useTranslations();
    const result = T.translate("issues");
    if (lowerCase && typeof result === "string") {
      return result.toLowerCase();
    }
    return result;
  },
  PickerModalTitle: ({ showTabTitle = true }) => {
    const { T } = useTranslations();
    const value = T.translate("referencePickerPlugin.addReference");
    return (
      <>
        {showTabTitle && <TabTitle value={value as string} />}
        {value}
      </>
    );
  },

  AddRelationshipsButtonText: ({ count }) => {
    const { T } = useTranslations();
    if (count === 1) {
      return T.translate("referencePickerPlugin.addOneRelationship");
    }
    return T.translate("referencePickerPlugin.addMultipleRelationships");
  },

  SelectedCount: ({ count }) => {
    const { T } = useTranslations();
    return T.translate("referencePickerPlugin.issuesSelected", { count });
  },

  queryEntities: async ({
    projectId,
    pageLimit,
    filters: { searchExpression, ...filters },
  }) => {
    const offset = (filters.offset as number) || 0;
    const sdk = createSDK({ projectId });
    try {
      const { results: data, pagination } = await sdk.Issues.getAll({
        limit: pageLimit,
        offset: offset as number,
        filterBy: {
          search: searchExpression ? `~%${searchExpression}%` : "",
          ...filters,
        },
        sortBy: {
          attribute: IssueSortingField.DisplayId,
          direction: IssueSortingDirection.Desc,
        },
      });
      const hasNextPage = pagination.totalResults > offset + pageLimit;
      return {
        data,
        nextPageQuery: hasNextPage
          ? {
              projectId,
              pageLimit,
              filters: { ...filters, offset: offset + pageLimit },
            }
          : undefined,
      };
    } catch {
      throw new Error("Failed to fetch");
    }
  },
  getEntitiesByIds: async ({ ids, projectId }) => {
    const sdk = createSDK({ projectId });
    try {
      const { results: data } = await sdk.Issues.getAll({
        filterBy: {
          id: ids,
        },
      });
      return { data };
    } catch {
      return { data: [] };
    }
  },
  FiltersPanel,
  EntityBlock: IssuesEntityBlock,
  canCreate: async ({ projectId }: { projectId: string }) => {
    const sdk = createSDK({ projectId });
    const currentUserPermissions = await sdk.CurrentUserPermissions.get();

    const hasCreatePermission =
      currentUserPermissions.permissionLevels.includes(
        CurrentUserPermissionLevel.Create
      );

    // Can't use `useFeatureFlag` here; relying on the presence of `allTypes`
    // (controlled by BE FF) to determine if visibility-by-type is on.
    const isVisibilityByTypeOn = currentUserPermissions.allTypes !== undefined;

    const hasAllTypes = !!currentUserPermissions.allTypes;
    const hasAnyType =
      (currentUserPermissions.issues.new.permittedTypes?.length ?? 0) > 0;
    const hasPermittedTypes =
      !isVisibilityByTypeOn || hasAllTypes || hasAnyType;

    return hasCreatePermission && hasPermittedTypes;
  },
  CreateNewEntityPanel: CreateNewEntityPanel,
  ActivityCard: IssueActivityCard as (props: any) => ReactNode,
  Provider,
  EntityMenuItem: IssueEntityCellItem,
  CustomDataView: IssuesCustomDataView,
  CustomPickerEmptyState: IssuesEmptyState,
} as const;
